<template>
    <div>
        <b-row>
            <b-col
                cols="auto"
            >
                <b-container>
                    <slot name="title-line"></slot>
                    <SelectionActions
                        class="d-inline-block pt-2"
                        v-if="selectionActionsInTitle == true"
                        :selection-actions="selectionActions"
                        :selected-row-count="selectedRows.length"
                        @action-clicked="handleActionClicked"
                    >
                    </SelectionActions>
                </b-container>
            </b-col>
        </b-row>
        <b-row>
            <b-col v-if="includeAutomaticSearch">
                <p-input
                    type="search"
                    v-model="automaticSearch"
                    :placeholder="searchMessageComputed"
                    @input="handleSearchInput"
                ></p-input>
            </b-col>
            <b-col v-if="includeAutomaticSearch && includeDropdown" cols="4">
                <p-select
                    v-model="searchBy"
                    :options="searchByOptions"
                    noSelectionMessage="All"
                />
            </b-col>
            <b-col
                cols="auto"
                v-if="showTopPerPageSelector && filteredRecordCount > minPerPage"
            >
                <p-select :options="perPageOptions" v-model="localPerPage" class="per-page-select" />
            </b-col>
            <b-col
                cols="auto"
                v-if="showPerPageSelector && filteredRecordCount > minPerPage && showTopPagination">
                <b-pagination
                    v-if="pagination && filteredRecordCount > localPerPage"
                    v-model="currentPage"
                    :total-rows="filteredRecordCount"
                    :per-page="localPerPage"
                ></b-pagination>
            </b-col>
            <SelectionActions
                class="d-inline-block pt-2"
                v-if="selectionActionsInTitle == false"
                :selection-actions="selectionActions"
                :selectedRowCount="selectedRows.length"
                @action-clicked="handleActionClicked"
            >
            </SelectionActions>
        </b-row>
        <b-row>
            <b-col>
                <slot name="above-table"> </slot>
            </b-col>
        </b-row>
        <b-row class='px-2' :style="{'max-height' : tableScroll ? tableScrollHeight +'px' : null, 'overflow-y' : tableScroll ? 'auto' : null}">
            <b-table
                v-bind="$attrs"
                responsive
                striped
                hover
                small
                :busy="isBusy"
                :show-empty="showEmpty"
                ref="selectableTable"
                no-sort-reset
                must-sort
                select-mode="range"
                :items="filteredItems"
                :empty-text="emptyText"
                :current-page="currentPage"
                @refreshed="updateSelectAll"
                :per-page="localPerPage"
                v-model:sort-by="bvnSortBy"
                :fields="calculatedFields"
                :search-destination="searchDestination"
                @row-clicked="
                    (item) => {
                        if (enableRowSelection && !disableRowCheck(item)) {
                            toggleRowSelection(item);
                        }
                        rowClicked(item);
                    }
                "
                @context-changed="$emit('context-changed', $event)"
                sort-icon-left
                :tbody-tr-class="computedRowClass"
            >
                <template
                    v-for="(_, name) in $slots" :key="name"
                    v-slot:[name]="slotData"
                    ><slot :name="name" v-bind="slotData" />
                </template>
                <template v-slot:empty>
                    {{ emptyText || 'There are no records to show.' }}
                </template>
                <template v-slot:head(selected)="data">
                    <p-checkbox
                        v-if="!singleSelect && showSelectAll"
                        :model-value="allSelected"
                        @change="toggleSelectAll"
                        :label="selectionLabel"
                    ></p-checkbox>
                    <span v-else-if ="singleSelect">{{ singleSelectButtonLabel }}</span>
                </template>
                <template v-slot:cell(selected)="{ value, item }">
                    <span v-if="!singleSelect" @click.stop="onSelection(item)">
                        <p-checkbox
                            :model-value="isSelected(item)"
                            :disabled="disableRowCheck(item)"
                        ></p-checkbox>
                    </span>
                    <p-button v-else size="sm" @click="addSelection(item)">{{
                        singleSelectButtonLabel
                    }}</p-button>
                </template>
            </b-table>
        </b-row>
        <slot name="abovePaging"></slot>
        <b-row v-if='showRecordCount' :class="{ 'busy-table': isBusy }">
            <b-col class="d-flex pb-0 pt-1">
            <div class="mt-2 ms-2">{{ currentPageRecordCount }} of {{recordCount}} Items</div>
                <div class="ms-auto me-3">
                    <p-select
                        v-model="localPerPage"
                        v-if="
                            showPerPageSelector &&
                                filteredRecordCount > minPerPage
                        "
                        :options="perPageOptions"
                        class="per-page-select"
                    >
                    </p-select>
                </div>
                <b-pagination
                    v-if="pagination && filteredRecordCount > localPerPage"
                    v-model="currentPage"
                    :total-rows="filteredRecordCount"
                    :per-page="localPerPage"
                ></b-pagination>
            </b-col>
        </b-row>
    </div>
</template>

<script>
import { textIncludes, searchPlaceHolderFormatter } from '@/components/Common/Formatters.js';
import SelectionActions from '@/components/SelectionActions.vue';

export default {
    name: 'p-table',
    inheritAttrs: false,
    emits: ['context-changed', 'sort-by-change', 'sort-desc-change', 'update:modelValue', 'input', 'updated-table-data', 'row-clicked', 'submit-selection', 'toggle-selection'],
    props: {
        modelValue: { default: null },
        items: Array,
        sortBy: String,
        sortDesc: Boolean,
        pagination: {
            type: Boolean,
            default: true
        },
        isSelectedFunction: {
            type: Function,
            default: undefined
        },
        emptyText: String,
        perPage: {
            type: Number,
            default: 10
        },
        showPerPageSelector: {
            type: Boolean,
            default: false
        },
        showTopPerPageSelector: {
            type: Boolean,
            default: false
        },
        searchDestination: {
            type: String,
            default: null
        },
        selectable: {
            type: Boolean,
            default: false
        },
        selectionActions: {
            type: Array,
            default: () => []
        },
        selectionActionsInTitle: {
            type: Boolean,
            default: false
        },
        fields: {
            type: Array,
            default: () => null
        },
        singleSelect: {
            type: Boolean,
            default: false
        },
        singleSelectButtonLabel: {
            type: String,
            default: 'Add'
        },
        disableRowCheck: {
            type: Function,
            default: () => false
        },
        enableRowSelection: {
            type: Boolean,
            default: true
        },
        disableRowClickSelection: {
            type: Boolean,
            default: false
        },
        showRecordCount: {
            type: Boolean,
            default: true
        },
        showEmpty: {
            type: Boolean,
            default: true
        },
        isBusy: {
            type: Boolean,
            default: false
        },
        showSelectAll: {
            type: Boolean,
            default: true
        },
        selectAllLabel: {
            type: String,
            default: null
        },
        showTopPagination: {
            type: Boolean,
            default: false
        },
        tableScroll: {
            type: Boolean,
            default: false
        },
        tableScrollHeight: {
            type: Number,
            default: 700
        },
        rowClass: {
            type: [String, Function],
            default: null
        }        
    },
    computed: {
        value() { return this.modelValue; },
        visibleFields() {
            return (this.fields || []).filter(x => !x.hidden).map(f => {
                // Auto-center boolean toggle columns (isActive / active) so the
                // checkbox doesn't crowd against neighboring cell content.
                // Respect any explicit thClass/tdClass the caller already set.
                const key = (f.key || '').toString().toLowerCase();
                if (key === 'isactive' || key === 'active') {
                    return {
                        ...f,
                        thClass: f.thClass ?? 'text-center',
                        tdClass: f.tdClass ?? 'text-center'
                    };
                }
                return f;
            });
        },
        calculatedFields() {
            if (this.enableRowSelection) {
                return [
                    {
                        label: '',
                        key: 'selected',
                        sortable: false,
                        thStyle: { width: '1%', whiteSpace: 'nowrap' },
                        tdStyle: { width: '1%', whiteSpace: 'nowrap' }
                    },
                    ...this.visibleFields
                ];
            }

            return this.visibleFields;
        },
        includeAutomaticSearch() {
            return (
                this.searchDestination === null && this.searchFields.length > 0
            );
        },
        searchFields() {
            return (this.fields || []).filter(x => x.automaticSearch);
        },
        recordCount() {
            return this.items?.length ?? 0;
        },
        filteredRecordCount() {
            return this.filteredItems?.length ?? 0;
        },
        filteredItems() {
            const items = this.items || [];
            if (!this.includeAutomaticSearch || this.automaticSearch === '') {
                return items;
            }
            return items.filter(x => this.itemPassesFilter(x));
        },
        selectionLabel() {
            if (this.selectAllLabel !== null) {
                return this.selectAllLabel;
            }
            if (this.isSelectedFunction) {
                const count = (this.filteredItems || []).filter(i => this.isSelectedFunction(i)).length;
                return count > 0 ? `(${count})` : '';
            }
            return this.selectedRows.length > 0
                ? `(${this.selectedRows.length})`
                : '';
        },
        computedPerPage() {
            return this.localPerPage;
        },
        minPerPage() {
            return this.perPageOptions[0].value;
        },

        searchByLabels(){
            //falling back to key when label is not present to avoid showing 'null'.
            //We should avoid not providing a label on search fields because key
            //isn't formatted for user display (invoiceNumber instead of Invoice Number for example)
            return this.searchFields.map(x => x.label || x.key);
        },
        // p-select's internal filteredOptions watcher matches on `x.value`, so
        // pass options as {value, text} pairs (using label as both) rather than
        // raw field defs. Without this, selecting a non-default option causes
        // p-select to think the value is invalid and clear it back to null.
        searchByOptions(){
            return this.searchFields.map(x => {
                const label = x.label || x.key;
                return { value: label, text: label };
            });
        },

        searchMessageComputed() {
            return searchPlaceHolderFormatter(this.searchByLabels,this.searchBy);
        },
        currentPageRecordCount(){
            let maxRecords = this.currentPage * this.computedPerPage;
            let result = this.computedPerPage;

            if(maxRecords > this.recordCount)
            {
                let overCount = maxRecords - this.recordCount;
                result = this.computedPerPage - overCount;
            }

            if(this.filteredRecordCount < this.computedPerPage || this.computedPerPage == 0)
            {
                result = this.filteredRecordCount;
            }

            return result;
        },
        includeDropdown(){
            return this.searchFields.length > 1;
        }
    },
    data() {
        return {
            currentPage: 1,
            selectedRows: [],
            automaticSearch: '',
            allSelected: false,
            perPageOptions: [
                { value: 10, text: 'Show 10' },
                { value: 25, text: 'Show 25' },
                { value: 50, text: 'Show 50' },
                { value: 100, text: 'Show 100' },
                { value: 500, text: 'Show 500' }
            ],
            localPerPage: 10,
            searchBy: null,
            bvnSortBy: this.sortBy ? [{ key: this.sortBy, order: this.sortDesc ? 'desc' : 'asc' }] : [],
            lastEmittedSortKey: this.sortBy ?? null,
            lastEmittedSortDesc: !!this.sortDesc
        };
    },
    created() {
        this.localPerPage = this.perPage;

        if(this.items != null && Array.isArray(this.items))
        {
            this.items.forEach(x => {this.isSelected(x)})
        }
    },
    watch: {
        bvnSortBy: {
            handler() {
                this.flushSortChange();
            },
            deep: true
        },
        isBusy(newVal) {
            // When a parent-driven sort/load finishes, flush any header clicks
            // the user made while we were waiting — only the latest one fires,
            // so rapid clicks during slow queries don't stack API requests.
            if (!newVal) this.flushSortChange();
        },
        currentPage: function() {
            // bootstrap-vue-next's BTable no longer emits the Vue 2
            // `context-changed` event, and pagination is driven by this
            // component's own currentPage. Re-emit it here so consumers that
            // re-render per-page content (e.g. DataImporter revalidating the
            // freshly-rendered rows) still get notified on a page change.
            this.emitContextChanged();
        },
        localPerPage: function() {
            // Changing page size resets to page 1. If we were already on page 1
            // the currentPage watcher won't fire, so emit here to cover both.
            if (this.currentPage === 1) {
                this.emitContextChanged();
            }
            this.currentPage = 1;
        },
        items: {
            handler: function() {
                let previousSelectionCount = this.selectedRows.length;
                this.selectedRows = this.selectedRows.filter(x =>
                    (this.items || []).find(y => y === x)
                );
                if(previousSelectionCount > this.selectedRows.length){
                    this.$emit('update:modelValue', this.selectedRows);
                    this.$emit('input', this.selectedRows);
                }
                if(this.filteredItems.length === 0){
                    this.currentPage = 1;
                }
                else if (this.currentPage > Math.ceil(this.filteredItems.length / this.perPage)) {
                    this.currentPage = Math.ceil(
                        this.filteredItems.length / this.perPage
                    );
                }
            },
            deep: true
        },
        filteredItems: function(){
            // Prune any previously-selected rows that have been filtered out so the
            // selection count matches what the user can currently see (Vue 2 behavior).
            if (!this.isSelectedFunction && this.selectedRows.length > 0) {
                const visible = this.filteredItems || [];
                const previousCount = this.selectedRows.length;
                this.selectedRows = this.selectedRows.filter(x => visible.includes(x));
                if (this.selectedRows.length !== previousCount) {
                    this.$emit('update:modelValue', this.selectedRows);
                    this.$emit('input', this.selectedRows);
                }
            }
            this.$nextTick(() => this.updateSelectAll());
            this.$emit('updated-table-data', this.filteredItems);
        }
    },
    methods: {
        emitContextChanged() {
            // Mirror the old bootstrap-vue context payload so existing
            // consumers reading $event keep working.
            this.$emit('context-changed', {
                currentPage: this.currentPage,
                perPage: this.localPerPage,
                sortBy: this.bvnSortBy?.[0]?.key ?? null,
                sortDesc: this.bvnSortBy?.[0]?.order === 'desc'
            });
        },
        flushSortChange() {
            if (this.isBusy) return;
            const current = this.bvnSortBy?.[0];
            if (!current) return;
            const desc = current.order === 'desc';
            if (current.key === this.lastEmittedSortKey && desc === this.lastEmittedSortDesc) return;
            this.lastEmittedSortKey = current.key;
            this.lastEmittedSortDesc = desc;
            this.$emit('sort-by-change', current.key);
            this.$emit('sort-desc-change', desc);
        },
        computedRowClass(item, type) {
            let classes = '';
            if (!this.isSelectedFunction && !this.disableRowClickSelection && this.selectedRows.some(x => x === item)) {
                classes += 'b-table-row-selected ';
            }
            if (type === 'row' && item && this.disableRowCheck?.(item)) {
                classes += 'p-table-row-disabled ';
            }
            if (typeof this.rowClass === 'function') {
                const extra = this.rowClass(item, type);
                if (extra) classes += extra;
            } else if (this.rowClass) {
                classes += this.rowClass;
            }
            return classes.trim() || null;
        },
        updateSelectAll() {
            const selectable = this.filteredItems?.filter(x => !this.disableRowCheck?.(x)) || [];
            if (this.isSelectedFunction) {
                this.allSelected = selectable.length > 0 && selectable.every(i => this.isSelectedFunction(i));
            } else {
                this.allSelected = selectable.length > 0 && selectable.every(i => this.selectedRows.includes(i));
            }
        },
        toggleSelectAll() {
            if (this.isSelectedFunction) {
                const selectable = this.filteredItems?.filter(x => !this.disableRowCheck?.(x)) || [];
                selectable.forEach(item => {
                    const selected = this.isSelectedFunction(item);
                    if (this.allSelected && selected) {
                        this.$emit('toggle-selection', item);
                    } else if (!this.allSelected && !selected) {
                        this.$emit('toggle-selection', item);
                    }
                });
                this.$nextTick(() => this.updateSelectAll());
                return;
            }
            this.selectedRows = this.allSelected
                ? []
                : [
                      ...(this.filteredItems || []).filter(
                          x => !this.disableRowCheck?.(x)
                      )
                  ];
            this.$emit('update:modelValue', this.selectedRows);
            this.$emit('input', this.selectedRows);
            this.updateSelectAll();
        },
        selectAll() {
            this.selectedRows = [
                      ...(this.filteredItems || []).filter(
                          x => !this.disableRowCheck?.(x)
                      )
                  ];
            this.$emit('update:modelValue', this.selectedRows);
            this.$emit('input', this.selectedRows);
        },
        unselectAll() {
            this.selectedRows = [];
            this.$emit('update:modelValue', this.selectedRows);
            this.$emit('input', this.selectedRows);
        },
        isSelected(item) {
            if (this.isSelectedFunction) {
                return this.isSelectedFunction(item);
            }
            return this.selectedRows.some(x => x === item);
        },
        itemPassesFilter(item) {
            let search;
            if (this.searchBy != null) {
                search = this.searchFields.filter(y => y.label === this.searchBy);

            } else {
                search = this.searchFields;
            }
            
           return search.some(x =>
                    textIncludes(x.key.split('.')
                      .reduce((p,c) => (x.formatter != null && x.formatter(p[c])) || (p && p[c]) || null, item ) + '', this.automaticSearch));
        },
        toggleRowSelection(item) {
            this.onSelection(item);
        },
        rowClicked(element) {
            this.$emit('row-clicked', element);
            this.$emit('update:modelValue', this.selectedRows);
            this.$emit('input', this.selectedRows);
        },
        handleSearchInput() {
            this.currentPage = 1;
        },
        addSelections(items) {
            (items || []).forEach(x => {
                if(!this.isSelected(x) && (this.items || []).some(y => y === x)){
                    this.selectedRows.push(x);
                }
            })
            this.$emit('update:modelValue', this.selectedRows);
            this.$emit('input', this.selectedRows);
            this.$emit('submit-selection');
            this.updateSelectAll();
        },
        addSelection(item) {
            if(!this.isSelected(item)  && (this.items || []).find(y => y === item)){
                this.selectedRows.push(item);
            }
            this.$emit('update:modelValue', this.selectedRows);
            this.$emit('input', this.selectedRows);
            this.$emit('submit-selection');
            this.updateSelectAll();
        },
        onSelection(item) {
            if (this.isSelectedFunction) {
                this.$emit('toggle-selection', item);
                setTimeout(() => this.updateSelectAll(), 50);
                return;
            }
            var existingIndex = this.selectedRows.findIndex(x => x === item);
            if (existingIndex > -1) {
                this.selectedRows.splice(existingIndex, 1);
            } else {
                this.selectedRows.push(item);
            }
            this.$emit('update:modelValue', this.selectedRows);
            this.$emit('input', this.selectedRows);
        },
        handleActionClicked(action) {
            action(this.selectedRows);
        }
    },
    components: {
        SelectionActions
    }
};
</script>

<style scoped lang="scss">
@import '@/styles/app.scss';

:deep(.table-responsive ) {
    overflow-y: visible;
    overflow-x: auto;
}
:deep(th) {
    user-select: none;
}
/* Keep sort arrow on the same line as the header text instead of wrapping below. */
:deep(thead th),
:deep(thead th *) {
    white-space: nowrap !important;
}
:deep(td) {
    vertical-align: middle;
}
:deep(th, td ) {
    max-width: 500px;
}
:deep(.mw120 ) {
    max-width: 120px;
}

:deep(tr.b-table-row-selected),
:deep(tr.b-table-row-selected td) {
    background-color: transparent !important;
    color: $body-color !important;
}
:deep(.table-hover tbody tr:hover td) {
    background-color: $gray-lighter !important;
}
:deep(.busy-table ) {
    opacity: 50%;
}
:deep(tr.p-table-row-disabled) > td {
    opacity: 0.4;
    cursor: not-allowed;
}
:deep(.table-hover tbody tr.p-table-row-disabled:hover) > td {
    background-color: transparent !important;
}
:deep(.noWrap ) {
    white-space: nowrap;
}

:deep(.tableScroll ) {
    max-height: 700px;
    overflow-y: auto;
}

</style>
<style lang="scss">
.form-select.per-page-select {
    min-width: 115px;
}
</style>
